// MyAnalyses — v1.1.0 (Saved Analyses)
//
// Lists the signed-in user's saved analyses, newest first, with per-row
// "Open in Deal Analyzer" and "Delete" actions. Read access is scoped by
// the analyses table's RLS policy (migration 013), so the query is a plain
// SELECT — Postgres restricts it to the current user's rows automatically.
//
// "Open in Deal Analyzer" hands the full saved row to DealAnalyzer via
// React Router state. DealAnalyzer's buildInitialForm() reads it and seeds
// the form, so the user can review, optionally adjust, and re-analyze.

import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listAnalyses, deleteAnalysis } from "../lib/analyses";
import {
  savedAnalysisViewed,
  savedAnalysisOpened,
  savedAnalysisDeleted,
} from "../lib/savedAnalysesAnalytics";
import { InfoTooltip, METRIC_TOOLTIPS } from "../components/InfoTooltip";
import NotAdviceDisclaimer from "../components/NotAdviceDisclaimer";

// =========================
// FORMATTERS
// =========================
function usd(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return `$${Math.round(Number(n)).toLocaleString()}`;
}
function num(n, digits = 2) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return Number(n).toFixed(digits);
}
function shortDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Map a recommendation decision string to the design system badge class.
// Matches the helper in DealAnalyzer so the same mental model carries over:
// BUY/HOLD/PASS/LEASE get their semantic badges; CONSIDER uses the steel-blue
// info family (--color-consider) via .badge-new.
function badgeClassFor(decision) {
  const d = String(decision || "").toUpperCase();
  if (d === "BUY")      return "badge-buy";
  if (d === "HOLD")     return "badge-hold";
  if (d === "PASS")     return "badge-pass-rec";
  if (d === "LEASE")    return "badge-lease";
  if (d === "CONSIDER") return "badge-new";
  return "";
}

// =========================
// MAIN PAGE
// =========================
export default function MyAnalyses() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const { data, error: listError } = await listAnalyses();
    if (listError) {
      setError(listError.message || "Could not load your saved analyses.");
      setRows([]);
    } else {
      const loaded = Array.isArray(data) ? data : [];
      setRows(loaded);
      // Customer value — saved-analysis return behavior (M-017). Count only.
      savedAnalysisViewed({ count: loaded.length });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function handleOpen(row) {
    // Customer value — saved-analysis reopen (M-018). No PII (source enum only).
    savedAnalysisOpened({ source: "list" });
    // Pass the full saved row to DealAnalyzer so its buildInitialForm()
    // can re-hydrate every field — financial inputs come from row.result.input,
    // address/lat-lng from the row's denormalized columns.
    navigate("/deal-analyzer", { state: { prefill: row } });
  }

  async function handleDelete(row) {
    const label = row.formatted_address || "this analysis";
    const ok = window.confirm(`Delete ${label}? This can't be undone.`);
    if (!ok) return;

    setDeletingId(row.id);
    const { error: deleteError } = await deleteAnalysis(row.id);
    setDeletingId(null);

    if (deleteError) {
      setError(deleteError.message || "Could not delete that analysis.");
      return;
    }
    // Optimistic local removal — RLS already confirmed the delete succeeded.
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    // Customer value — saved-analysis deletion.
    savedAnalysisDeleted();
  }

  return (
    <div
      style={{
        background: "var(--color-navy)",
        minHeight: "100%",
        padding: "var(--space-6)",
        color: "var(--color-white)",
        fontFamily: "var(--font-body)",
      }}
    >
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        {/* ── Page header ───────────────────────────────────── */}
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "var(--space-4)",
            marginBottom: "var(--space-6)",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "var(--text-4xl)",
                fontWeight: "var(--weight-black)",
                letterSpacing: "var(--tracking-tight)",
                textTransform: "uppercase",
                color: "var(--color-white)",
                margin: 0,
                marginBottom: "var(--space-2)",
              }}
            >
              My Analyses
            </h1>
            <p
              style={{
                color: "var(--color-steel-lt)",
                fontSize: "var(--text-md)",
                margin: 0,
              }}
            >
              {loading
                ? "Loading your saved analyses…"
                : rows.length === 0
                ? "Nothing saved here yet."
                : `${rows.length} saved ${rows.length === 1 ? "analysis" : "analyses"}, newest first.`}
            </p>
          </div>
          <Link to="/deal-analyzer" className="btn btn-primary">
            + New analysis
          </Link>
        </header>

        {/* PLATFORM-002 (WS2) — ADR-044 disclaimer on the Saved Analyses surface
            (recommendation + deal score are shown per card below). */}
        <div style={{ marginTop: "calc(-1 * var(--space-4))", marginBottom: "var(--space-5)" }}>
          <NotAdviceDisclaimer style={{ color: "var(--color-steel-lt)", fontSize: "var(--text-sm)" }} />
        </div>

        {/* ── Error banner ──────────────────────────────────── */}
        {error && (
          <div
            role="alert"
            style={{
              marginBottom: "var(--space-5)",
              padding: "var(--space-4) var(--space-5)",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--color-danger)",
              background: "var(--color-danger-ghost)",
              color: "var(--color-danger-lt)",
              fontWeight: "var(--weight-semibold)",
              fontSize: "var(--text-base)",
            }}
          >
            {error}
          </div>
        )}

        {/* ── Empty state ───────────────────────────────────── */}
        {!loading && rows.length === 0 && !error && <EmptyState />}

        {/* ── Analysis cards ────────────────────────────────── */}
        <div style={{ display: "grid", gap: "var(--space-5)" }}>
          {rows.map((row) => (
            <AnalysisCard
              key={row.id}
              row={row}
              onOpen={() => handleOpen(row)}
              onDelete={() => handleDelete(row)}
              deleting={deletingId === row.id}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// =========================
// ANALYSIS CARD
// =========================
function AnalysisCard({ row, onOpen, onDelete, deleting }) {
  const address = row.formatted_address || "Unspecified address";
  const decision = row.recommendation || null;
  const badgeClass = badgeClassFor(decision);

  return (
    <article className="card">
      {/* ── Card header — address + saved meta | recommendation badge ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "var(--space-4)",
          flexWrap: "wrap",
          marginBottom: "var(--space-4)",
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "var(--text-2xl)",
              fontWeight: "var(--weight-bold)",
              color: "var(--color-white)",
              marginBottom: "var(--space-1)",
              wordBreak: "break-word",
              lineHeight: "var(--leading-tight)",
            }}
          >
            {address}
          </div>
          <div style={{ fontSize: "var(--text-sm)", color: "var(--color-steel)" }}>
            Saved {shortDate(row.created_at)}
          </div>
        </div>
        {decision && (
          <span className={`badge ${badgeClass}`}>{decision}</span>
        )}
      </div>

      {/* ── Metrics grid ─────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: "var(--space-3)",
          marginBottom: "var(--space-5)",
        }}
      >
        <Metric label="Monthly Revenue" value={usd(row.monthly_revenue)} tooltip={METRIC_TOOLTIPS.projectedMonthlyRevenue} />
        <Metric label="NOI"             value={usd(row.noi)}             tooltip={METRIC_TOOLTIPS.noi} />
        <Metric label="DSCR"            value={num(row.dscr)}            tooltip={METRIC_TOOLTIPS.dscr} />
        <Metric label="Deal Score"      value={row.deal_score ?? "—"}    tooltip={METRIC_TOOLTIPS.dealScore} />
      </div>

      {/* ── Actions ──────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <button type="button" onClick={onOpen} className="btn btn-primary">
          Open in Deal Analyzer
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          className="btn btn-danger"
          style={{ marginLeft: "auto" }}
        >
          {deleting ? "Deleting…" : "Delete"}
        </button>
      </div>
    </article>
  );
}

// =========================
// METRIC — small labeled value tile (mirrors Leads' Metric helper)
// =========================
function Metric({ label, value, tooltip }) {
  return (
    <div className="card-stat" style={{ padding: "var(--space-3) var(--space-4)" }}>
      <div
        className="card-stat-label"
        style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-2)" }}
      >
        <span>{label}</span>
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
      <div
        className="card-stat-value"
        style={{
          fontSize: "var(--text-xl)",
          color: "var(--color-white)",
        }}
      >
        {value}
      </div>
    </div>
  );
}

// =========================
// EMPTY STATE
// =========================
function EmptyState() {
  return (
    <div
      className="card"
      style={{
        textAlign: "center",
        padding: "var(--space-12) var(--space-6)",
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "var(--text-2xl)",
          fontWeight: "var(--weight-bold)",
          color: "var(--color-white)",
          marginBottom: "var(--space-2)",
        }}
      >
        No saved analyses yet
      </div>
      <p
        style={{
          fontSize: "var(--text-base)",
          lineHeight: "var(--leading-normal)",
          color: "var(--color-steel-lt)",
          maxWidth: 520,
          margin: "0 auto",
          marginBottom: "var(--space-6)",
        }}
      >
        Run a property in the{" "}
        <strong style={{ color: "var(--color-amber)" }}>Deal Analyzer</strong>{" "}
        and click <strong style={{ color: "var(--color-amber)" }}>Save analysis</strong>{" "}
        on the results page. Your saved analyses will show up here so you can
        re-open or re-run them later.
      </p>
      <Link to="/deal-analyzer" className="btn btn-primary btn-lg">
        Open the Deal Analyzer
      </Link>
    </div>
  );
}
